import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getGuestList } from '../lib/tables';
import { Check, Clock, MapPin } from 'lucide-react';
import type { RegistryEvent } from '../types/registry';
import type { GuestListItem } from '../types/tables';

interface Props {
  registryId: string;
  events: RegistryEvent[];
}

interface RSVPFormData {
  name: string;
  email: string;
  phone: string;
  numberOfGuests: number;
  status: 'attending' | 'not_attending';
  mealPreference: string;
  dietaryRestrictions: string;
  plusOne: boolean;
  plusOneName: string;
  comments: string;
  selectedEvents: string[];
}

export default function RSVPForm({ registryId, events }: Props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [allGuests, setAllGuests] = useState<GuestListItem[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<GuestListItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [formData, setFormData] = useState<RSVPFormData>({
    name: '',
    email: '',
    phone: '',
    numberOfGuests: 1,
    status: 'attending',
    mealPreference: '',
    dietaryRestrictions: '',
    plusOne: false,
    plusOneName: '',
    comments: '',
    selectedEvents: events.length > 0 ? [events[0].id] : []
  });

  // Load guest list on component mount
  React.useEffect(() => {
    loadGuestList();
  }, [registryId]);

  const loadGuestList = async () => {
    try {
      setLoadingGuests(true);
      const guests = await getGuestList(registryId);
      setAllGuests(guests);
    } catch (err) {
      console.error('Error loading guest list:', err);
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleNameChange = (value: string) => {
    setFormData({ ...formData, name: value });
    
    if (value.trim().length >= 2) {
      const filtered = allGuests.filter(guest =>
        guest.guest_name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (guestName: string) => {
    setFormData({ ...formData, name: guestName });
    setShowSuggestions(false);
    setFilteredSuggestions([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      // Only validate against guest list if one has been uploaded
      if (allGuests.length > 0) {
        const guestExists = allGuests.some(guest =>
          guest.guest_name.toLowerCase() === formData.name.trim().toLowerCase()
        );
        if (!guestExists) {
          setError('Sorry, your name is not found in the guest list. Please contact the event organizer if you believe this is an error.');
          return;
        }
      }

      // Create RSVP entries for each selected event
      for (const eventId of formData.selectedEvents) {
        const { error: rsvpError } = await supabase
          .from('registry_rsvps')
          .insert({
            registry_id: registryId,
            event_id: eventId,
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            number_of_guests: formData.numberOfGuests,
            status: formData.status,
            meal_preference: formData.mealPreference || null,
            dietary_restrictions: formData.dietaryRestrictions || null,
            plus_one: formData.plusOne,
            plus_one_name: formData.plusOneName || null,
            comments: formData.comments || null
          });

        if (rsvpError) throw rsvpError;
      }

      setSuccess(true);
      
      // Redirect to registry page after a short delay
      setTimeout(() => {
        navigate(`/registries/${registryId}`);
      }, 2000);

    } catch (err) {
      console.error('Error submitting RSVP:', err);
      setError('Failed to submit RSVP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelect = (eventId: string) => {
    setFormData(prev => {
      const selectedEvents = prev.selectedEvents.includes(eventId)
        ? prev.selectedEvents.filter(id => id !== eventId)
        : [...prev.selectedEvents, eventId];
      return { ...prev, selectedEvents };
    });
  };

  if (success) {
    return (
      <div className="bg-green-50 backdrop-blur-sm p-6 rounded-lg text-center space-y-4 animate-fade-in">
        <div className="flex items-center justify-center">
          <Check className="w-12 h-12 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 font-poppins">Thank You!</h3>
        <p className="text-gray-600">Your RSVP has been submitted successfully.</p>
        <p className="text-gray-600">Redirecting to the gift registry...</p>
      </div>
    );
  }

  return (
    <div className="card-pastel rounded-lg p-6 animate-fade-in">
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="space-y-6">
          <h3 className="text-lg font-medium font-poppins text-gray-800">
            We can't wait to celebrate with you!
          </h3>

          {events.length > 0 && (
            <div className="space-y-4">
             <h3 className="text-xl font-semibold font-poppins text-gray-800 mb-6">Available Events</h3>
              <div className="grid gap-4">
                {events.map((event) => (
                  <label
                    key={event.id}
                   className={`block p-6 rounded-xl border-2 transition-all duration-200 cursor-pointer shadow-soft-lg hover:shadow-soft-lg transform hover:-translate-y-1 ${
                      formData.selectedEvents.includes(event.id)
                       ? 'bg-white/80 border-primary shadow-primary/20'
                       : 'bg-white/60 border-white/40 hover:border-pastel-purple-200 hover:bg-white/80'
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={formData.selectedEvents.includes(event.id)}
                        onChange={() => handleEventSelect(event.id)}
                       className="mt-2 h-5 w-5 rounded border-white/40 bg-white/60 text-primary focus:ring-primary focus:ring-offset-2 focus:ring-offset-white"
                      />
                     <div className="ml-4 space-y-3 flex-1">
                        <div className="flex items-center justify-between">
                         <h4 className="text-xl font-bold font-poppins text-gray-800">{event.name}</h4>
                         <span className="text-base font-medium text-gray-600 bg-white/60 px-3 py-1 rounded-full ml-4">
                            {new Date(event.date).toLocaleDateString()}
                          </span>
                        </div>
                       <div className="space-y-2 text-sm">
                          <div className="flex items-center">
                           <Clock className="mr-3 h-4 w-4 text-pastel-blue-600" />
                           <span className="text-gray-600">
                              {event.start_time}
                              {event.end_time && ` - ${event.end_time}`}
                            </span>
                          </div>
                          <div className="flex items-center">
                           <MapPin className="mr-3 h-4 w-4 text-pastel-green-600" />
                           <span className="text-gray-600">{event.venue}</span>
                          </div>
                        </div>
                        {event.dress_code && (
                         <div className="bg-white/60 p-3 rounded-lg">
                           <p className="text-sm">
                             <span className="font-medium text-gray-800">Dress Code:</span>
                             <span className="ml-2 text-gray-600">{event.dress_code}</span>
                           </p>
                         </div>
                       )}
                       {event.parking_info && (
                         <div className="bg-white/60 p-3 rounded-lg">
                           <p className="text-sm">
                             <span className="font-medium text-gray-800">Parking:</span>
                             <span className="ml-2 text-gray-600">{event.parking_info}</span>
                           </p>
                         </div>
                       )}
                       {event.special_instructions && (
                         <div className="bg-white/60 p-3 rounded-lg">
                           <p className="text-sm">
                             <span className="font-medium text-gray-800">Special Instructions:</span>
                             <span className="ml-2 text-gray-600">{event.special_instructions}</span>
                          </p>
                         </div>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Your Name *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onFocus={() => {
                    if (filteredSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  className="input-pastel"
                  placeholder={loadingGuests ? "Loading guest list..." : "Start typing your name..."}
                  required
                  disabled={loadingGuests}
                />
                
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white/80 border border-white/40 rounded-md shadow-soft-lg max-h-60 overflow-y-auto">
                    <ul className="py-1">
                      {filteredSuggestions.slice(0, 10).map((guest, index) => (
                        <li
                          key={index}
                          className="px-4 py-2 hover:bg-white/60 cursor-pointer text-gray-800"
                          onClick={() => handleSuggestionClick(guest.guest_name)}
                        >
                          <div className="flex items-center">
                            <span>{guest.guest_name}</span>
                            {guest.contact_info && (
                              <span className="text-sm text-gray-500 ml-2">({guest.contact_info})</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-pastel"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-pastel"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Number of Guests *
              </label>
              <input
                type="number"
                value={formData.numberOfGuests}
                onChange={(e) => setFormData({ ...formData, numberOfGuests: Number(e.target.value) })}
                min="1"
                className="input-pastel"
                required
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium font-poppins text-gray-800">Attendance</h3>
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                checked={formData.status === 'attending'}
                onChange={() => setFormData({ ...formData, status: 'attending' })}
                className="text-primary focus:ring-primary"
              />
              <span className="text-gray-700">Yes, I'll be there</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                checked={formData.status === 'not_attending'}
                onChange={() => setFormData({ ...formData, status: 'not_attending' })}
                className="text-primary focus:ring-primary"
              />
              <span className="text-gray-700">No, I can't make it</span>
            </label>
          </div>
        </div>

        {formData.status === 'attending' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Meal Preference
                </label>
                <select
                  value={formData.mealPreference}
                  onChange={(e) => setFormData({ ...formData, mealPreference: e.target.value })}
                  className="input-pastel"
                >
                  <option value="">Select a preference</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="vegan">Vegan</option>
                  <option value="non-vegetarian">Non-vegetarian</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Dietary Restrictions
                </label>
                <input
                  type="text"
                  value={formData.dietaryRestrictions}
                  onChange={(e) => setFormData({ ...formData, dietaryRestrictions: e.target.value })}
                  className="input-pastel"
                  placeholder="Any allergies or restrictions"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.plusOne}
                  onChange={(e) => setFormData({ ...formData, plusOne: e.target.checked })}
                  className="text-primary focus:ring-primary rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  I'm bringing a plus one
                </label>
              </div>

              {formData.plusOne && (
                <input
                  type="text"
                  value={formData.plusOneName}
                  onChange={(e) => setFormData({ ...formData, plusOneName: e.target.value })}
                  className="input-pastel"
                  placeholder="Guest's name"
                />
              )}
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            Additional Comments
          </label>
          <textarea
            value={formData.comments}
            onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
            className="input-pastel"
            rows={3}
            placeholder="Any additional information or special requests"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || formData.selectedEvents.length === 0}
            className="px-6 py-2 btn-pastel text-white rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Submit RSVP'}
          </button>
        </div>
      </form>
    </div>
  );
}
